import React, { useEffect, useState } from 'react';
import { BookOpen, Loader2, Plus } from 'lucide-react';
import { knowledgeAPI } from '../services/api';

const Knowledge: React.FC = () => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [items, setItems] = useState<
    Array<{
      id: string;
      source_type: string;
      source_ref: string | null;
      preview: string;
      created_at: string;
    }>
  >([]);

  const load = async () => {
    setListLoading(true);
    try {
      const data = await knowledgeAPI.list(40);
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!content.trim()) {
      setMessage({ type: 'err', text: 'Please enter the information guests should know.' });
      return;
    }
    setLoading(true);
    try {
      const res = await knowledgeAPI.add({
        title: title.trim() || undefined,
        content: content.trim(),
      });
      setMessage({
        type: 'ok',
        text: `Saved. ${res.chunksAdded} chunk(s) added to your hotel knowledge base.`,
      });
      setTitle('');
      setContent('');
      await load();
    } catch (err: any) {
      setMessage({
        type: 'err',
        text: err?.response?.data?.error || 'Could not save. Try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen className="h-8 w-8 text-blue-guestby" />
        <h1 className="text-2xl font-bold text-slate-900">Hotel knowledge</h1>
      </div>
      <p className="text-slate-600 mb-8">
        Add facts, policies, or answers that were missing from the AI. They are embedded and used only for your
        hotel, so future guest questions can be answered automatically.
      </p>

      <form onSubmit={submit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-10">
        <label className="block text-sm font-medium text-slate-700 mb-1">Title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Pool hours"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-4 focus:ring-2 focus:ring-blue-guestby focus:border-transparent"
        />
        <label className="block text-sm font-medium text-slate-700 mb-1">Information for guests</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          placeholder="Clear, factual text the concierge can rely on…"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-4 focus:ring-2 focus:ring-blue-guestby focus:border-transparent font-mono text-sm"
        />
        {message && (
          <p
            className={`text-sm mb-4 ${message.type === 'ok' ? 'text-emerald-700' : 'text-red-600'}`}
          >
            {message.text}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 bg-blue-guestby text-black-guestby font-medium px-5 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Add to knowledge base
        </button>
      </form>

      <h2 className="text-lg font-semibold text-slate-900 mb-3">Recently added chunks</h2>
      {listLoading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-slate-500 text-sm">No entries yet. PDFs uploaded in Settings also appear here as chunks.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((row) => (
            <li
              key={row.id}
              className="bg-white border border-slate-200 rounded-lg p-4 text-sm text-slate-700"
            >
              <div className="flex justify-between gap-4 text-xs text-slate-500 mb-1">
                <span>{row.source_type}</span>
                <span>{row.created_at ? new Date(row.created_at).toLocaleString() : ''}</span>
              </div>
              <p className="whitespace-pre-wrap">{row.preview}{row.preview.length >= 200 ? '…' : ''}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Knowledge;
